import React, {useEffect, useState} from 'react'
import {Box, CircularProgress, Stack, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import Apiservices from '../../../../lib/ApiServices'
import {RootState} from '../../../store'
import MainBtn from '../../customTable/MainBtn'
import {
  CheckoutSummary,
  getGatewayDisplay,
  PaymentPurpose,
} from '../../../utils/gatewayDisplay'
import {formatPaymentDisplay, resolveLocalizedString} from '../../../utils/paymentHelpers'

const VENDOR_API_ORIGIN = 'https://vendor.api.koninstore.com'

const PRIMARY = '#2C3E6B'
const SURFACE = '#F0F3F7'
const MUTED = '#65656A'
const WHITE = '#FFFFFF'
const BORDER = '#E5E7EB'

type GatewayRow = {
  id: number
  country_id: number
  factory_name: string
  purpose: string
  is_enabled: boolean
  sort_order: number
}

interface Props {
  open: boolean
  onClose: () => void
  onBack?: () => void
  amount: number
  currency: string
  purpose: PaymentPurpose
  countryId: number
  referenceId: number
  onGatewaySelected: (factoryName: string) => void
  summary?: CheckoutSummary
  isPaying?: boolean
  confirmTextKey?: string
}

function GatewayLogo({
  src,
  fallbackSrc,
  width,
  height,
  alt,
}: {
  src: string
  fallbackSrc: string
  width: number
  height: number
  alt: string
}) {
  const [imgSrc, setImgSrc] = useState(src)

  return (
    <Box
      sx={{
        width: {xs: Math.min(width, 100), sm: width},
        height: {xs: Math.min(height, 50), sm: height},
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: WHITE,
        borderRadius: '8px',
        border: `1px solid ${BORDER}`,
        p: {xs: '6px', sm: '8px'},
        flexShrink: 0,
      }}
    >
      <Box
        component='img'
        src={imgSrc}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
        onError={() => {
          if (imgSrc !== fallbackSrc) {
            setImgSrc(fallbackSrc)
          }
        }}
      />
    </Box>
  )
}

export default function PaymentGatewaySelector({
  open,
  onClose,
  amount,
  currency,
  purpose,
  countryId,
  referenceId,
  onGatewaySelected,
  summary,
  isPaying = false,
  confirmTextKey = 'payNow',
}: Props) {
  const {t} = useTranslation()
  const store = useSelector((state: RootState) => state.vender)
  const [gateways, setGateways] = useState<GatewayRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const [selectedFactory, setSelectedFactory] = useState<string | null>(null)

  void referenceId

  useEffect(() => {
    if (!open) {
      setSelectedFactory(null)
      return
    }

    if (!countryId) {
      return
    }

    let isCancelled = false

    const loadGateways = async () => {
      setIsLoading(true)
      setHasError(false)

      try {
        const res = await Apiservices.get(
          `${VENDOR_API_ORIGIN}/api/countries/${countryId}/payment-gateways`,
          {params: {purpose}}
        )
        const rows: GatewayRow[] = res?.data?.data?.gateways || []

        if (!isCancelled) {
          setGateways(rows)
          // Pre-select only when a single gateway exists; still render the list
          if (rows.length === 1) {
            setSelectedFactory(rows[0].factory_name)
          }
        }
      } catch {
        if (!isCancelled) {
          setGateways([])
          setHasError(true)
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    loadGateways()

    return () => {
      isCancelled = true
    }
  }, [open, countryId, purpose, fetchKey])

  const selectedDisplay = selectedFactory ? getGatewayDisplay(selectedFactory) : null
  const titleKey = summary?.titleKey || 'completePayment'
  const amountLabel =
    formatPaymentDisplay(summary?.priceLabel || amount, currency) ||
    formatPaymentDisplay(amount, currency)
  const planNameLabel = resolveLocalizedString(summary?.planName, store.lang, store.defLang)

  const metaParts = [
    summary?.durationLabel,
    summary?.startDateLabel ? `${t('startDate')}: ${summary.startDateLabel}` : '',
  ]
    .filter(Boolean)
    .join(' | ')

  const handleConfirm = () => {
    if (!selectedFactory || isPaying) {
      return
    }
    onClose()
    onGatewaySelected(selectedFactory)
  }

  return (
    <Stack
      p={{xs: '16px', sm: '24px', lg: '32px'}}
      gap={{xs: '16px', sm: '20px', lg: '24px'}}
      dir={store.isRtl !== 0 ? 'rtl' : 'ltr'}
      bgcolor={WHITE}
      sx={{maxWidth: '100%', overflowX: 'hidden'}}
    >
      <Stack gap='6px'>
        <Typography
          sx={{
            fontSize: {xs: '16px', sm: '18px', lg: '20px'},
            fontWeight: 700,
            color: PRIMARY,
          }}
        >
          {t(titleKey)}
        </Typography>
        <Typography sx={{fontSize: {xs: '12px', sm: '14px'}, fontWeight: 500, color: MUTED}}>
          {t('selectPaymentMethod')}
        </Typography>
      </Stack>

      {/* Order summary */}
      <Box
        sx={{
          bgcolor: SURFACE,
          borderRadius: '8px',
          border: `1px solid ${BORDER}`,
          p: {xs: '12px', sm: '16px', lg: '20px'},
        }}
      >
        <Stack
          direction='row'
          gap={{xs: '10px', sm: '16px'}}
          alignItems='center'
          justifyContent='space-between'
        >
          <Stack direction='row' gap='10px' alignItems='center' minWidth={0} flex={1}>
            {summary?.imageSrc ? (
              <Box
                component='img'
                src={summary.imageSrc}
                alt=''
                sx={{
                  width: {xs: 40, sm: 56},
                  height: {xs: 40, sm: 56},
                  borderRadius: '8px',
                  objectFit: 'cover',
                  flexShrink: 0,
                  bgcolor: WHITE,
                  display: {xs: 'none', sm: 'block'},
                }}
              />
            ) : null}
            <Stack gap='2px' minWidth={0}>
              {planNameLabel ? (
                <Typography
                  sx={{
                    fontSize: {xs: '14px', sm: '16px'},
                    fontWeight: 700,
                    color: PRIMARY,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {planNameLabel}
                </Typography>
              ) : null}
              {metaParts ? (
                <Typography
                  sx={{
                    fontSize: {xs: '11px', sm: '13px'},
                    color: MUTED,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {metaParts}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          <Typography
            sx={{
              fontSize: {xs: '18px', sm: '22px', lg: '24px'},
              fontWeight: 700,
              color: PRIMARY,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {amountLabel}
          </Typography>
        </Stack>
      </Box>

      {isLoading && (
        <Stack alignItems='center' py='20px'>
          <CircularProgress size={32} sx={{color: PRIMARY}} />
        </Stack>
      )}

      {!isLoading && hasError && (
        <Stack alignItems='center' gap='12px' py='12px'>
          <Typography sx={{fontSize: '13px', color: MUTED, textAlign: 'center'}}>
            {t('paymentGatewaysLoadError')}
          </Typography>
          <MainBtn width='auto' handelSubmit={() => setFetchKey((key) => key + 1)} text='retry' />
        </Stack>
      )}

      {!isLoading && !hasError && gateways.length === 0 && (
        <Typography sx={{fontSize: '13px', color: MUTED, textAlign: 'center', py: '12px'}}>
          {t('noPaymentMethodAvailable')}
        </Typography>
      )}

      {/* xs: 1 col | sm/md tablet: 2 | lg desktop: 3 */}
      {!isLoading && !hasError && gateways.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: gateways.length === 1 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              lg: `repeat(${Math.min(gateways.length, 3)}, minmax(0, 1fr))`,
            },
            gap: {xs: '12px', sm: '16px'},
          }}
        >
          {gateways.map((gateway) => {
            const factoryName = gateway.factory_name
            const display = getGatewayDisplay(factoryName)
            const isSelected = selectedFactory === factoryName

            return (
              <Box
                key={gateway.id}
                component='button'
                type='button'
                onClick={() => setSelectedFactory(factoryName)}
                sx={{
                  display: 'flex',
                  flexDirection: {xs: 'row', sm: 'column'},
                  alignItems: 'center',
                  gap: {xs: '12px', sm: '10px'},
                  width: '100%',
                  p: {xs: '12px', sm: '16px', lg: '20px'},
                  border: isSelected ? `2px solid ${PRIMARY}` : `1px solid ${BORDER}`,
                  borderRadius: '8px',
                  backgroundColor: isSelected ? SURFACE : WHITE,
                  cursor: 'pointer',
                  textAlign: {xs: 'start', sm: 'center'},
                  position: 'relative',
                  minHeight: {xs: 'auto', sm: 0},
                  transition: 'border-color 0.15s ease, background-color 0.15s ease',
                  '&:hover': {
                    borderColor: PRIMARY,
                    backgroundColor: SURFACE,
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: {xs: 10, sm: 12},
                    insetInlineEnd: {xs: 10, sm: 12},
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: isSelected ? `2px solid ${PRIMARY}` : `2px solid ${BORDER}`,
                    bgcolor: isSelected ? PRIMARY : WHITE,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isSelected ? (
                    <Typography sx={{color: WHITE, fontSize: '11px', fontWeight: 700, lineHeight: 1}}>
                      ✓
                    </Typography>
                  ) : null}
                </Box>

                <GatewayLogo
                  src={display.iconSrc}
                  fallbackSrc={display.iconFallbackSrc}
                  width={display.logoWidth}
                  height={display.logoHeight}
                  alt={t(display.nameKey)}
                />

                <Stack
                  gap='2px'
                  alignItems={{xs: 'flex-start', sm: 'center'}}
                  flex={1}
                  minWidth={0}
                  pr={{xs: '24px', sm: 0}}
                >
                  <Typography sx={{fontSize: {xs: '14px', sm: '15px', lg: '16px'}, fontWeight: 700, color: PRIMARY}}>
                    {t(display.nameKey)}
                  </Typography>
                  {display.descriptionKey ? (
                    <Typography
                      sx={{
                        fontSize: {xs: '12px', sm: '13px'},
                        color: MUTED,
                        lineHeight: 1.4,
                        textAlign: {xs: 'start', sm: 'center'},
                      }}
                    >
                      {t(display.descriptionKey)}
                    </Typography>
                  ) : null}
                </Stack>
              </Box>
            )
          })}
        </Box>
      )}

      {selectedDisplay?.flowInfoKey ? (
        <Box
          sx={{
            bgcolor: 'rgba(55, 138, 221, 0.08)',
            borderRadius: '8px',
            border: '1px solid rgba(55, 138, 221, 0.18)',
            p: {xs: '12px', sm: '16px'},
            my: '4px',
          }}
        >
          <Typography sx={{fontSize: '13px', color: PRIMARY, lineHeight: 1.5, fontWeight: 500}}>
            {t(selectedDisplay.flowInfoKey)}
          </Typography>
        </Box>
      ) : null}

      {/* Cancel outline + Pay solid */}
      <Stack direction='row' gap='12px' pt='4px' alignItems='stretch' width='100%'>
        <Box sx={{flex: '1 1 0', minWidth: 0}}>
          <MainBtn
            width='100%'
            minWidth='0'
            height='52px'
            fs='15px'
            handelSubmit={onClose}
            text='cancel'
            without
            disabled={isPaying}
          />
        </Box>
        <Box sx={{flex: '1.4 1 0', minWidth: 0}}>
          <MainBtn
            width='100%'
            minWidth='0'
            height='52px'
            fs='16px'
            bgcolor={PRIMARY}
            handelSubmit={handleConfirm}
            text={confirmTextKey}
            isLoading={isPaying}
            disabled={!selectedFactory || isPaying || gateways.length === 0}
          />
        </Box>
      </Stack>

      <Typography
        sx={{
          fontSize: '12px',
          color: MUTED,
          lineHeight: 1.4,
          mt: '8px',
          pt: '8px',
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        {t('vat_note')}
      </Typography>
    </Stack>
  )
}
